import { Component, signal, OnInit, OnDestroy, computed, inject, ElementRef } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { NgIcon } from '@ng-icons/core';
import { AuthService } from './core/services/auth.service';
import { AppRoleService } from './core/services/app-role.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, NgIcon],
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class App implements OnInit, OnDestroy {
  // Signal for user menu dropdown
  showUserMenu = signal(false);
  showAdminMenu = signal(false);

  // Inject services using inject() function
  protected readonly authService = inject(AuthService);
  protected readonly appRoleService = inject(AppRoleService);
  private readonly router = inject(Router);

  // Expose auth signals to template
  readonly account = this.authService.account;
  readonly isAuthenticated = this.authService.isAuthenticated;

  // Computed display name
  readonly displayName = computed(() => {
    const account = this.account();
    if (!account) return 'Guest';
    return account.name || account.username || 'User';
  });

  // Computed user email
  readonly userEmail = computed(() => {
    const account = this.account();
    return account?.username || '';
  });

  private readonly lastNavEvent = toSignal(
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
    { initialValue: null }
  );

  readonly isAdminActive = computed(() => {
    this.lastNavEvent(); // re-evaluate on every navigation
    return this.router.url.startsWith('/admin');
  });

  private readonly hostRef = inject(ElementRef);
  private documentClickHandler!: (event: MouseEvent) => void;

  ngOnInit(): void {
    // Load role once after auth is ready.
    // waitForAuthentication() resolves after MSAL's async redirect handling,
    // so this fires even when the account signal was null at construction time.
    void this.authService.waitForAuthentication().then((authenticated) => {
      if (authenticated) void this.appRoleService.loadCurrentRole();
    });

    // Close user menu when clicking outside
    this.documentClickHandler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (this.showUserMenu()) {
        const menu = this.hostRef.nativeElement.querySelector('.avatar-btn');
        const dropdown = this.hostRef.nativeElement.querySelector('.origin-top-right');
        if (menu && !menu.contains(target) && dropdown && !dropdown.contains(target)) {
          this.showUserMenu.set(false);
        }
      }
      if (this.showAdminMenu()) {
        const adminBtn = this.hostRef.nativeElement.querySelector('.admin-btn');
        const adminDropdown = this.hostRef.nativeElement.querySelector('.admin-dropdown');
        if (adminBtn && !adminBtn.contains(target) && adminDropdown && !adminDropdown.contains(target)) {
          this.showAdminMenu.set(false);
        }
      }
    };
    document.addEventListener('mousedown', this.documentClickHandler, true);
  }

  ngOnDestroy(): void {
    if (this.documentClickHandler) {
      document.removeEventListener('mousedown', this.documentClickHandler, true);
    }
  }

  async onLogout(): Promise<void> {
    this.showUserMenu.set(false);
    await this.authService.signOut();
    // Navigate to login without any query params, replacing history
    await this.router.navigate(['/login'], { 
      queryParams: {},
      replaceUrl: true 
    });
  }
}
